import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { Building2, Download, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import MobileHeaderToggle from "../../GlobalManagement/Pages/MobileHeaderToggle";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell 
} from '../../GlobalManagement/Pages/masterPageStyles';

const pad = (n) => String(n).padStart(2, "0");

const toDDMMYYYY = (value) => {
  if (!value || value === "-") return "-";
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return raw;
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
  }
  return raw;
};

const toISODate = (value) => {
  if (!value || value === "-") return "";
  const raw = String(value).trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  return "";
};

const getStatusClass = (status, isLateEntry) => {
  if (isLateEntry && String(status || "").toUpperCase() === "PRESENT") {
    return "bg-orange-100 text-orange-700";
  }
  const s = String(status || "").toUpperCase();
  if (s === "PRESENT") return "bg-green-100 text-green-700";
  if (s === "ABSENT") return "bg-red-100 text-red-700";
  if (s === "OFF" || s === "OFF(EXCH)") return "bg-gray-100 text-gray-700";
  if (s.startsWith("CL")) return "bg-blue-100 text-blue-700";
  if (s.startsWith("SL")) return "bg-purple-100 text-purple-700";
  return "bg-yellow-100 text-yellow-700";
};

const getDisplayStatus = (status, isLateEntry) =>
  isLateEntry ? "LATE ENTRY" : status;

const normalizeStatus = (status) => String(status || "").trim().toUpperCase();
const normalizeEmployeeId = (value) => String(value || "").trim().toUpperCase();
const isExEmployeeId = (value) => normalizeEmployeeId(value).startsWith("EX-");
const getEmployeePrefix = (value) => {
  const normalized = normalizeEmployeeId(value);
  if (!normalized || isExEmployeeId(normalized)) return "";
  return normalized.split("-")[0] || "";
};

const AttendanceRegisterHistory = () => {
  const PER_PAGE_STORAGE_KEY = "attendanceRegisterHistory.pageSize";
  const MONTH_STORAGE_KEY = "attendanceRegisterHistory.selectedMonth";
  
  const getStoredPageSize = () => {
    const raw = localStorage.getItem(PER_PAGE_STORAGE_KEY);
    if (!raw) return 18;
    if (raw === "all" || raw === "-1") return -1;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : 18;
  };

  const getStoredMonth = () => {
    const raw = localStorage.getItem(MONTH_STORAGE_KEY);
    if (raw && /^\d{4}-\d{2}$/.test(raw)) return raw;
    const now = new Date();
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  };

  const [selectedMonth, setSelectedMonth] = useState(getStoredMonth);
  const [employees, setEmployees] = useState([]);
  const [attendanceDocs, setAttendanceDocs] = useState([]);
  const [departments, setDepartments] = useState(["ALL"]);
  const [designations, setDesignations] = useState(["ALL"]);
  const [selectedDepartment, setSelectedDepartment] = useState("ALL");
  const [selectedDesignation, setSelectedDesignation] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedEmployeePrefix, setSelectedEmployeePrefix] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  const [loading, setLoading] = useState(false);
  const tableContainerRef = useRef(null);

  const normalizeSearchInput = (value) => {
    let v = String(value || "").toUpperCase();
    if (/^[A-Z]+\d/.test(v)) {
      v = v.replace(/^([A-Z]+)-?(\d.*)$/, "$1-$2");
    }
    return v;
  };

  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [empRes, deptRes] = await Promise.all([
          axios.get("/api/employees"),
          axios.get("/api/departments"),
        ]);
        const empList = empRes.data || [];
        setEmployees(empList.filter((emp) => !isExEmployeeId(emp.employeeID)));
        setDepartments(["ALL", ...(deptRes.data || []).map((d) => d.deptName)]);
      } catch (err) {
        console.error("Failed to load base data", err);
      }
    };

    fetchBaseData();
  }, []);

  useEffect(() => {
    let scopedEmployees = employees;
    if (selectedDepartment !== "ALL") {
      scopedEmployees = employees.filter(
        (emp) => emp.departmentName === selectedDepartment
      );
    }
    const uniqueDesignations = [
      "ALL",
      ...new Set(scopedEmployees.map((emp) => emp.designationName).filter(Boolean)),
    ];
    setDesignations(uniqueDesignations);
  }, [employees, selectedDepartment]);

  const employeePrefixOptions = useMemo(() => {
    const prefixes = employees
      .map((emp) => getEmployeePrefix(emp.employeeID))
      .filter(Boolean);
    return ["ALL", ...new Set(prefixes)];
  }, [employees]);

  useEffect(() => {
    const fetchAttendance = async () => {
      if (!selectedMonth || !/^\d{4}-\d{2}$/.test(selectedMonth)) {
        setAttendanceDocs([]);
        return;
      }
      setLoading(true);
      try {
        const [yearStr, monthStr] = selectedMonth.split("-");
        const month = Number(monthStr);
        const year = Number(yearStr);
        const res = await axios.get(
          `/api/attendance/history?month=${month}&year=${year}`
        );
        setAttendanceDocs(res.data || []);
      } catch (err) {
        console.error("Failed to load attendance", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [selectedMonth]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMonth, selectedDepartment, selectedDesignation, selectedStatus, selectedEmployeePrefix, searchTerm, pageSize]);

  useEffect(() => {
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem(MONTH_STORAGE_KEY, selectedMonth);
  }, [selectedMonth]);

  const rows = useMemo(() => {
    const employeeMap = {};
    employees.forEach((emp) => {
      if (emp.employeeUserId) employeeMap[emp.employeeUserId] = emp;
      if (emp.employeeID) employeeMap[emp.employeeID] = emp;
    });

    const flattened = [];
    attendanceDocs.forEach((doc) => {
      const mappedEmployee =
        employeeMap[doc.employeeUserId] || employeeMap[doc.employeeId] || {};

      (doc.records || []).forEach((record) => {
        const resolvedEmployeeId = doc.employeeId || mappedEmployee.employeeID || "-";
        if (isExEmployeeId(resolvedEmployeeId)) return;

        flattened.push({
          rawDate: record.date || "-",
          rawDateISO: toISODate(record.date),
          date: toDDMMYYYY(record.date || "-"),
          employeeId: resolvedEmployeeId,
          employeeUserId:
            doc.employeeUserId || mappedEmployee.employeeUserId || "-",
          employeeName:
            doc.employeeName ||
            `${mappedEmployee.firstName || ""} ${mappedEmployee.lastName || ""}`.trim() ||
            "-",
          departmentName: mappedEmployee.departmentName || "-",
          designationName: mappedEmployee.designationName || "-",
          shiftCode: record.shiftCode || "-",
          shiftStartTime: record.shiftStartTime || "-",
          shiftEndTime: record.shiftEndTime || "-",
          checkInTime: record.checkInTime || "-",
          checkOutTime: record.checkOutTime || "-",
          workDuration: record.workDuration || "-",
          actualWorkDuration: record.actualWorkDuration || "-",
          status: record.status || "-",
          isLateEntry: !!(record.isLateEntry || (record.status === "Present" && record.isLate)),
          otHours: Number(record.otHours || 0),
        });
      });
    });

    flattened.sort((a, b) => {
      const dateDiff = new Date(b.rawDate) - new Date(a.rawDate);
      if (dateDiff !== 0) return dateDiff;
      return String(a.employeeName).localeCompare(String(b.employeeName));
    });

    return flattened;
  }, [attendanceDocs, employees]);

  const filteredRows = useMemo(() => {
    const q = String(searchTerm || "").toUpperCase().trim();
    return rows.filter((row) => {
      const rowMonth = row.rawDateISO ? row.rawDateISO.substring(0, 7) : "";
      const monthMatch = rowMonth === selectedMonth;

      const departmentMatch =
        selectedDepartment === "ALL" || row.departmentName === selectedDepartment;
      const designationMatch =
        selectedDesignation === "ALL" || row.designationName === selectedDesignation;
      const prefixMatch =
        selectedEmployeePrefix === "ALL" ||
        getEmployeePrefix(row.employeeId) === selectedEmployeePrefix;
      const statusUpper = normalizeStatus(row.status);
      const statusMatch =
        selectedStatus === "ALL" ||
        (selectedStatus === "PRESENT" &&
          (statusUpper === "PRESENT" || statusUpper === "P" || statusUpper === "P(L)")) ||
        (selectedStatus === "ABSENT" && (statusUpper === "ABSENT" || statusUpper === "A")) ||
        (selectedStatus === "OFF" && (statusUpper === "OFF" || statusUpper === "OFF(EXCH)")) ||
        (selectedStatus === "CL" && statusUpper.startsWith("CL")) ||
        (selectedStatus === "SL" && statusUpper.startsWith("SL")) ||
        (selectedStatus === "LATEENTRY" && row.isLateEntry);

      if (!monthMatch || !departmentMatch || !designationMatch || !prefixMatch || !statusMatch) return false;
      if (!q) return true;

      const name = String(row.employeeName || "").toUpperCase();
      const userId = String(row.employeeUserId || "").toUpperCase();
      const employeeId = String(row.employeeId || "").toUpperCase();
      return (
        name.includes(q) || userId.includes(q) || employeeId.includes(q)
      );
    });
  }, [rows, selectedMonth, selectedDepartment, selectedDesignation, selectedStatus, selectedEmployeePrefix, searchTerm]);

  const startIndex = pageSize === -1 ? 0 : (currentPage - 1) * pageSize;
  const paginatedRows =
    pageSize === -1
      ? filteredRows
      : filteredRows.slice(startIndex, startIndex + pageSize);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 400;
      tableContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

const handleExportExcel = () => {
    const groupedByDate = {};
    filteredRows.forEach((row) => {
      const d = row.rawDateISO || row.date;
      if (!groupedByDate[d]) groupedByDate[d] = [];
      groupedByDate[d].push(row);
    });

    const exportData = [];
    let sl = 1;

    Object.keys(groupedByDate).sort().forEach((dateKey) => {
      const formattedDate = toDDMMYYYY(dateKey);

      groupedByDate[dateKey].forEach((row) => {
        exportData.push({
          "SL": sl++,
          "Date": formattedDate,
          "Employee ID": row.employeeId,
          "User ID": row.employeeUserId,
          "Name": row.employeeName,
          "Department": row.departmentName,
          "Designation": row.designationName,
          "Shift": row.shiftCode,
          "Shift Start": row.shiftStartTime,
          "Shift End": row.shiftEndTime,
          "Punch In": row.checkInTime,
          "Punch Out": row.checkOutTime,
          "Work Time": row.workDuration,
          "Actual Work": row.actualWorkDuration,
          "Status": getDisplayStatus(row.status, row.isLateEntry),
          "OT Hours": row.otHours,
        });
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Attendance");
    XLSX.writeFile(workbook, `Attendance_Register_Month_${selectedMonth}.xlsx`);
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Building2 size={14} /></div>
              <div><h1 className={masterTitle}>Attendance Register History</h1></div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportExcel}
                className={masterAccentButton}
              >
                <Download size={10} /> Export Excel
              </button>
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex-1 flex flex-col min-h-0`}>
          <MobileHeaderToggle>
            <div className={`${masterPanel} p-3 mb-3 border border-slate-200 bg-white`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">
                    Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 bg-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">
                    Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => {
                      setSelectedDepartment(e.target.value);
                      setSelectedDesignation("ALL");
                    }}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {departments.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {designations.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">
                    Status
                  </label>
                  <select
                    value={selectedEmployeePrefix}
                    onChange={(e) => setSelectedEmployeePrefix(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {employeePrefixOptions.map((prefix) => (
                      <option key={prefix} value={prefix}>
                        {prefix}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="font-semibold text-slate-700 text-[11px] uppercase mb-1">
                    Attendance
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="border border-slate-300 rounded px-2.5 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="ALL">ALL</option>
                    <option value="PRESENT">Present</option>
                    <option value="ABSENT">Absent</option>
                    <option value="OFF">OFF</option>
                    <option value="CL">CL</option>
                    <option value="SL">SL</option>
                    <option value="LATEENTRY">Late Entry</option>
                  </select>
                </div>
              </div>
            </div>
          </MobileHeaderToggle>

          <div className={`${masterPanel} flex flex-col overflow-hidden w-full flex-1 relative`}>
            {/* SearchBar with Integrated Pagination */}
            <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2 z-20">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(normalizeSearchInput(val));
                  setCurrentPage(1);
                }} 
                placeholder="Search name / user ID / emp ID..." 
                totalItems={filteredRows.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>

            {/* Horizontal Scroll Arrows */}
            <button 
              type="button"
              onClick={() => scrollTable('left')}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Left"
            >
              <ChevronLeft size={20} />
            </button>

            <button 
              type="button"
              onClick={() => scrollTable('right')}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-50 bg-white/90 hover:bg-white text-green-600 shadow-md border border-slate-200 p-2 rounded-full transition-all active:scale-90 hidden md:flex items-center justify-center"
              title="Scroll Right"
            >
              <ChevronRight size={20} />
            </button>

            <div 
              ref={tableContainerRef} 
              className="w-full flex-1 min-h-0 overflow-auto bg-white rounded-lg shadow border border-slate-200 scroll-smooth relative"
            >
              <table className={`${masterTable} min-w-[1500px]`}>
                <thead>
                  <tr className={masterStickyHeadRow}>
                    <th className={masterTableData} style={{ width: '50px' }}>SL</th>
                    <th className={masterTableData}>Date</th>
                    <th className={masterTableData}>Employee ID</th>
                    <th className={masterTableData}>User ID</th>
                    <th className={masterTableData}>Name</th>
                    <th className={masterTableData}>Department</th>
                    <th className={masterTableData}>Designation</th>
                    <th className={masterTableData}>Shift</th>
                    <th className={masterTableData}>Shift Start</th>
                    <th className={masterTableData}>Shift End</th>
                    <th className={masterTableData}>Punch In</th>
                    <th className={masterTableData}>Punch Out</th>
                    <th className={masterTableData}>Work Time</th>
                    <th className={masterTableData}>Actual Work</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>OT Hours</th>
                  </tr>
                </thead>
                <tbody className={masterTbody}>
                  {loading ? (
                    <tr>
                      <td colSpan="16" className="text-center py-16 bg-white">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <Loader2 className="animate-spin text-slate-600" size={24} />
                          <span className="text-xs font-semibold text-slate-600">
                            Loading Attendance Data... Please wait...
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedRows.length > 0 ? (
                    paginatedRows.map((row, idx) => (
                      <tr
                        key={`${row.employeeUserId}-${row.date}-${idx}`}
                        className={`${masterTableRow} ${idx % 2 === 0 ? "bg-slate-50/50" : "bg-white"}`}
                      >
                        <td className={`${masterTableCell} text-center`}>
                          {pageSize === -1 ? idx + 1 : startIndex + idx + 1}
                        </td>
                        <td className={masterTableCell}>{row.date}</td>
                        <td className={masterTableCell}>{row.employeeId}</td>
                        <td className={masterTableCell}>{row.employeeUserId}</td>
                        <td className={`${masterTableCell} uppercase font-medium`}>{row.employeeName}</td>
                        <td className={masterTableCell}>{row.departmentName}</td>
                        <td className={masterTableCell}>{row.designationName}</td>
                        <td className={masterTableCell}>{row.shiftCode}</td>
                        <td className={masterTableCell}>{row.shiftStartTime}</td>
                        <td className={masterTableCell}>{row.shiftEndTime}</td>
                        <td className={masterTableCell}>{row.checkInTime}</td>
                        <td className={masterTableCell}>{row.checkOutTime}</td>
                        <td className={masterTableCell}>{row.workDuration}</td>
                        <td className={masterTableCell}>{row.actualWorkDuration}</td>
                        <td className={masterTableCell}>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getStatusClass(row.status, row.isLateEntry)}`}>
                            {getDisplayStatus(row.status, row.isLateEntry)}
                          </span>
                        </td>
                        <td className={`${masterTableCell} text-right font-bold`}>{row.otHours}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="16" className="p-12 text-center text-slate-400 font-bold uppercase text-xs">
                        No attendance register data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AttendanceRegisterHistory;